import { parse } from 'qs';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import Icon from 'dpl/common/components/Icon';
import BreedSearch from 'dpl/components/BreedSearch';
import { BREED_PROFILES, BREED_TYPES } from 'dpl/components/BreedSearch/util';
import FancyDropdown, {
  FancyDropdownMenu,
  FancyDropdownMenuItem,
  FancyDropdownToggle
} from 'dpl/components/FancyDropdown';
import { SHELTERS_LOCATE } from 'dpl/constants/query_params';
import LocationAutocompleteContainer from 'dpl/search/containers/LocationAutocompleteContainer';
import { removeCountryFromLocationName } from 'dpl/search/utils/helpers';
import LocationResult from 'dpl/search_result_filters/components/UserLocation/LocationResult';
import { navigateToShelterResults } from 'dpl/shelter_search_results/utils';

import {
  HEADER_SEARCH_TYPE_LABELS,
  HEADER_SEARCH_TYPE_VALUES,
  HEADER_SEARCH_TYPES
} from '../components/constants';
import useInitialHeaderState from '../hooks/useInitialHeaderState';

const INPUT_CLASS_NAME = 'w-100 b--none br12 pv3 pl4 pr12';
const ICON_SIZE = '24px';

export default function DesktopSearchContainer() {
  const {
    searchType: initialSearchType,
    searchBreedName: initialBreedName,
    searchBreedResultsPath: initialBreedResultsPath,
    searchBreedOverviewPagePath: initialBreedOverviewPagePath
  } = useInitialHeaderState();

  const [searchType, setSearchType] = useState(initialSearchType);
  const [locationQuery, setLocationQuery] = useState('');
  const [breedQuery, setBreedQuery] = useState(initialBreedName);
  const [isOpen, setIsOpen] = useState(false);

  const [isSearchDropdownOpen, setIsSearchDropdownOpen] = useState(
    window.__DPL_BOT_UA
  );

  const isBreedGuidesSearch = searchType === HEADER_SEARCH_TYPES.BREED_GUIDES;
  const isSheltersSearch = searchType === HEADER_SEARCH_TYPES.SHELTERS;
  const isBreedersSearch = searchType === HEADER_SEARCH_TYPES.BREEDERS;
  const breedType = isBreedGuidesSearch ? BREED_PROFILES : BREED_TYPES.ALL;

  function handleDropdownChange(isOpenValue) {
    setIsOpen(isOpenValue);
  }

  function handleSearchDropdownChange(isOpenValue) {
    setIsSearchDropdownOpen(isOpenValue);
  }

  function handleBreedSearch(breed) {
    window.location.href = breed.url;
  }

  function handleLocationChange(e) {
    setLocationQuery(e.target.value);
  }

  function handleLocationSelect(location) {
    setLocationQuery(location.name);
    navigateToShelterResults(location);
  }

  function handleSearchClick() {
    if (
      !searchType ||
      !breedQuery ||
      (searchType === initialSearchType && breedQuery === initialBreedName)
    ) {
      return;
    }

    if (isBreedersSearch) {
      window.location.href = initialBreedResultsPath;
    } else if (isBreedGuidesSearch) {
      window.location.href = initialBreedOverviewPagePath;
    }
  }

  const hasBreedGuide = useSelector(({ server }) => {
    return (
      initialSearchType === HEADER_SEARCH_TYPES.BREED_GUIDES ||
      Boolean(server.breedOverviewPageUrl)
    );
  });

  const initialLocationName = useSelector(({ queryParams }) => {
    const { location, coordinates = {} } =
      parse(queryParams[SHELTERS_LOCATE])?.query || {};

    return removeCountryFromLocationName({
      name: location || coordinates.formatted_address || ''
    });
  });

  useEffect(() => {
    if (isBreedersSearch && !breedQuery) {
      setBreedQuery(initialBreedName);
    } else if (isBreedGuidesSearch && !hasBreedGuide) {
      setBreedQuery('');
    } else if (isSheltersSearch) {
      setLocationQuery(initialLocationName);
    }
  }, [
    initialBreedName,
    initialLocationName,
    breedQuery,
    isBreedGuidesSearch,
    isBreedersSearch,
    isSheltersSearch,
    hasBreedGuide
  ]);

  return (
    <div
      className="DesktopSearchContainer relative center flex-auto flex items-center font-16 br12 bg-white ba b--gray-200"
      style={{ maxWidth: isSheltersSearch ? '540px' : '470px' }}
    >
      <FancyDropdown
        isOpen={isOpen}
        onChange={handleDropdownChange}
        menuWidth="260px"
        allowPropagationOnClose
      >
        <FancyDropdownToggle buttonClassName="br12">
          <p className="DesktopSearchContainer__toggle pv3 ph4 link--primary flex gap-2 items-center br b--gray-200">
            <span className="secondary truncate">
              {HEADER_SEARCH_TYPE_LABELS[searchType]}
            </span>
            <Icon
              className="tertiary"
              name={`fetch-chevron-${isOpen ? 'up' : 'down'}`}
              height={ICON_SIZE}
              width={ICON_SIZE}
            />
          </p>
        </FancyDropdownToggle>
        <FancyDropdownMenu className="mt2 nr13" newDesignSystemStyles>
          {HEADER_SEARCH_TYPE_VALUES.map(value => (
            <FancyDropdownMenuItem
              key={value}
              itemKey={value}
              className="br2 overflow-hidden mv1"
              selectedClassName="bg-subtle-gray"
            >
              <button
                type="button"
                className="tertiary pointer w-100 pv3 ph4"
                onClick={() => setSearchType(value)}
              >
                {HEADER_SEARCH_TYPE_LABELS[value]}
              </button>
            </FancyDropdownMenuItem>
          ))}
        </FancyDropdownMenu>
      </FancyDropdown>
      <div className="flex-auto bl br b--transparent nr14">
        {(isBreedersSearch || isBreedGuidesSearch) && (
          <BreedSearch
            placeholder="Search a breed"
            className={INPUT_CLASS_NAME}
            inputClassName="fw-normal"
            isOpen={isSearchDropdownOpen}
            onChange={handleSearchDropdownChange}
            query={breedQuery}
            onSelectItem={handleBreedSearch}
            breedType={breedType}
            maxHeight="335px"
            isNewLayout
          />
        )}
        {isSheltersSearch && (
          <LocationAutocompleteContainer
            classes={{
              menu: 'mt3',
              selectedMenuItem: 'bg-subtle-gray br2'
            }}
            inputProps={{
              name: 'shelter-location',
              type: 'text',
              className: INPUT_CLASS_NAME,
              onChange: handleLocationChange,
              placeholder: 'Add your location'
            }}
            menuCloseTimeout={300}
            onSelect={handleLocationSelect}
            query={locationQuery}
            ResultsItemComponent={LocationResult}
            resultsItemProps={{
              classes: {
                wrapper: 'fw-medium ph4 pv3',
                icon: 'pv3 ph3'
              },
              iconSize: ICON_SIZE
            }}
            stripCountryCode
            newDesignSystemStyles
          />
        )}
      </div>
      <button
        type="button"
        onClick={handleSearchClick}
        className="flex items-center justify-center transition blue-500 hover-stone overflow-hidden br12 pv3 ph4 z-1"
      >
        <Icon name="fetch-search" height={ICON_SIZE} width={ICON_SIZE} />
      </button>
    </div>
  );
}
